import { cn } from "src/lib/cn";
import { Box, WithReset } from "src/sudokit/Layout/BoxReset";
import { Text } from "../Text";
import InlineSVG from "react-inlinesvg";
import { useEffect, useCallback, forwardRef } from "react";

/**
 * @typedef {Object} Option
 * @property {string} value - The option's value
 * @property {string} label - The option's display label
 */

/**
 * @param {Object} props
 * @param {Option[]} props.options - Array of options to display
 * @param {string[]} props.value - Array of selected values
 * @param {(values: string[]) => void} props.onChange - Callback when selection changes
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
export const MultiCheck = forwardRef(({ options, value = [], onChange, className }, ref) => {
  const handleToggle = useCallback(
    (optionValue) => {
      const newValue = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue];
      onChange(newValue);
    },
    [value, onChange]
  );

  useEffect(() => {
    const handleEnter = (event) => {
      if (event.key === "Enter") {
        event.stopPropagation();
        handleToggle(event.target.dataset.value);
      }
    };
    document.addEventListener("keydown", handleEnter);
    return () => document.removeEventListener("keydown", handleEnter);
  }, [handleToggle, value]);

  return (
    <div ref={ref} className={cn("flex flex-wrap gap-2", className)}>
      {options.map((option) => {
        const isSelected = value.includes(option.value);

        return (
          <WithReset key={`key-${option.value}`}>
            <Box
              tabIndex={0}
              data-value={option.value}
              as="button"
              onClick={() => handleToggle(option.value)}
              className={cn(
                "pr-2.5 pl-1 py-1 rounded-lg border transition-all duration-200 cursor-pointer active:scale-[0.98] hover:scale-[1.02] group focus-visible:outline focus-visible:outline-[var(--primary-cta)]",
                isSelected
                  ? "border-app-primary-cta-high-contrast text-app-primary-cta opacity-100 hover:bg-app-primary-cta-transparent"
                  : "bg-transparent border-app-grey-1 hover:border-app-grey-0 hover:text-app-grey-0 opacity-70 hover:opacity-80 hover:bg-app-grey-4"
              )}
            >
              <Box className="flex items-center gap-1">
                <InlineSVG
                  src="/assets/check-selected.svg"
                  className={cn("transition-colors", isSelected ? "fill-app-primary-cta-high-contrast [&>path]:fill-app-primary-cta-high-contrast" : "fill-app-grey-1")}
                />
                <Text
                  size="base"
                  weight="medium"
                  className={cn("transition-colors", isSelected ? "text-app-primary-cta-high-contrast" : "text-app-grey-0")}
                >
                  {option.label}
                </Text>
              </Box>
            </Box>
          </WithReset>
        );
      })}
    </div>
  );
});
